/**
 * FileStorageConfigPage.jsx
 * 
 * Admin configuration page for file storage settings.
 * Provides a clear visual representation of how files are organized within a single
 * SharePoint document library, with per-module folder paths.
 * 
 * Features:
 * - Configure SharePoint site URL and document library (the ONE library)
 * - Visual folder tree showing where each module stores files
 * - Editable path templates for advanced users
 * - File size and extension restrictions
 * - Test connection functionality
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    IoSave, IoChevronDown, IoChevronForward,
    IoFolderOpen, IoFolder, IoDocument,
    IoSettings, IoCloudDone, IoAlertCircle,
    IoCheckmarkCircle
} from 'react-icons/io5';
import * as Sentry from '@sentry/react';

/**
 * Module definitions — each module has a path template key, label, icon, and description.
 * These correspond to the PathTemplate* fields on the FileStorageSettings entity.
 */
const MODULE_DEFINITIONS = [
    {
        key: 'pathTemplateActionItems',
        label: 'Action Items',
        icon: '🎫',
        description: 'General action item attachments',
        example: 'ActionItems/P123/2025/AI567'
    },
    {
        key: 'pathTemplateSipa',
        label: 'SIPA Reports',
        icon: '📝',
        description: 'Safety Inspection & Property Assessment report files',
        example: 'SIPA/P123/2025/11/R456'
    },
    {
        key: 'pathTemplateSipaActionItems',
        label: 'SIPA Action Items',
        icon: '📋',
        description: 'Action items generated from SIPA inspections',
        example: 'SIPA/P123/2025/11/R456/AI789'
    },
    {
        key: 'pathTemplateAssets',
        label: 'Asset Management',
        icon: '🏗️',
        description: 'Asset photos, manuals, and documents',
        example: 'Assets/P123/C5/A789'
    },
    {
        key: 'pathTemplateNonCompliance',
        label: 'Non-Compliance',
        icon: '⚠️',
        description: 'Non-compliance inspection attachments',
        example: 'NonCompliance/P123/2025/11/AI567'
    },
    {
        key: 'pathTemplateProjects',
        label: 'Projects',
        icon: '📐',
        description: 'Project management documents',
        example: 'Projects/P123/PR456/2025'
    },
    {
        key: 'pathTemplateLegal',
        label: 'Legal',
        icon: '⚖️',
        description: 'Legal department files',
        example: 'Legal/P123/2025/11'
    },
    {
        key: 'pathTemplateLeasing',
        label: 'Leasing',
        icon: '📄',
        description: 'Lease agreements and tenant documents',
        example: 'Leasing/P123/T789/2025'
    },
    {
        key: 'pathTemplateFinance',
        label: 'Finance',
        icon: '💰',
        description: 'Financial documents and invoices',
        example: 'Finance/P123/2025/11'
    },
    {
        key: 'pathTemplateMarketing',
        label: 'Marketing',
        icon: '📢',
        description: 'Marketing materials and media',
        example: 'Marketing/P123/2025/11'
    },
    {
        key: 'pathTemplateIT',
        label: 'IT',
        icon: '💻',
        description: 'IT department and ticket files',
        example: 'IT/D5/2025/T789'
    },
    {
        key: 'pathTemplateOperations',
        label: 'Operations',
        icon: '🔧',
        description: 'Operations certificates and documents',
        example: 'Operations/P123/Certs/2025'
    },
    {
        key: 'pathTemplateUserFiles',
        label: 'User Files',
        icon: '👤',
        description: 'User profile photos and documents',
        example: 'Users/U456'
    },
    {
        key: 'pathTemplateTemp',
        label: 'Temporary Files',
        icon: '🕐',
        description: 'Auto-cleaned temporary uploads',
        example: '_Temp/2025/11/15'
    }
];

/**
 * FileStorageConfigPage Component
 */
const FileStorageConfigPage = () => {
    const { apiFetch, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingPaths, setSavingPaths] = useState(false);
    const [testing, setTesting] = useState(false);

    // Storage provider config (from AppSecrets/FileStorageConfig)
    const [config, setConfig] = useState({
        provider: 'SharePoint',
        sharePointSiteUrl: '',
        sharePointLibraryName: 'Action Item Attachments',
        sharePointFolderPath: '',
        localBasePath: '',
        maxFileSizeBytes: 52428800,
        allowedExtensions: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip',
        isEnabled: true
    });

    // Path template settings (from FileStorageSettings table)
    const [paths, setPaths] = useState({
        basePathPrefix: 'CapTrac',
        pathTemplateSipa: 'SIPA/P{PropertyID}/{Year}/{Month}/R{ReportID}',
        pathTemplateSipaActionItems: 'SIPA/P{PropertyID}/{Year}/{Month}/R{ReportID}/AI{ActionItemID}',
        pathTemplateAssets: 'Assets/P{PropertyID}/C{AssetCategoryID}/A{AssetID}',
        pathTemplateActionItems: 'ActionItems/P{PropertyID}/{Year}/AI{ActionItemID}',
        pathTemplateNonCompliance: 'NonCompliance/P{PropertyID}/{Year}/{Month}/AI{ActionItemID}',
        pathTemplateProjects: 'Projects/P{PropertyID}/PR{ProjectID}/{Year}',
        pathTemplateLegal: 'Legal/P{PropertyID}/{Year}/{Month}',
        pathTemplateLeasing: 'Leasing/P{PropertyID}/T{TenantID}/{Year}',
        pathTemplateFinance: 'Finance/P{PropertyID}/{Year}/{Month}',
        pathTemplateMarketing: 'Marketing/P{PropertyID}/{Year}/{Month}',
        pathTemplateIT: 'IT/D{DepartmentID}/{Year}/T{TicketID}',
        pathTemplateOperations: 'Operations/P{PropertyID}/Certs/{Year}',
        pathTemplateUserFiles: 'Users/U{UserID}',
        pathTemplateTemp: '_Temp/{Year}/{Month}/{Day}',
        preserveOriginalFileName: true,
        fileNamePrefix: 'Timestamp',
        sanitizeFileNames: true,
        maxFileNameLength: 100,
        tempFileRetentionDays: 30
    });

    // UI state
    const [expandedModule, setExpandedModule] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        fetchAll();
    }, []);

    // Track changes to avoid auto-saving on mount.
    // Counter: 0 = not loaded, 1 = initial load applied (skip), 2+ = user changed.
    const configChangeCountRef = useRef(0);
    const pathsChangeCountRef = useRef(0);

    // Auto-save config with debounce — only fires after user changes
    useEffect(() => {
        if (loading) return;
        configChangeCountRef.current += 1;
        if (configChangeCountRef.current <= 1) return;

        const timer = setTimeout(() => {
            handleSaveConfig();
        }, 800);
        return () => clearTimeout(timer);
    }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save paths with debounce — only fires after user changes
    useEffect(() => {
        if (loading) return;
        pathsChangeCountRef.current += 1;
        if (pathsChangeCountRef.current <= 1) return;

        const timer = setTimeout(() => {
            handleSavePaths();
        }, 800);
        return () => clearTimeout(timer);
    }, [paths]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchConfiguration(), fetchPaths()]);
        setLoading(false);
    };

    const fetchConfiguration = async () => {
        try {
            const response = await apiFetch('/api/Settings/file-storage-config');
            const data = await response.json();
            if (data.success && data.data) {
                setConfig(prev => ({
                    ...prev,
                    ...data.data,
                    sharePointSiteUrl: data.data.sharePointSiteUrl ?? '',
                    sharePointLibraryName: data.data.sharePointLibraryName ?? prev.sharePointLibraryName,
                    sharePointFolderPath: data.data.sharePointFolderPath ?? '',
                    localBasePath: data.data.localBasePath ?? '',
                    allowedExtensions: data.data.allowedExtensions ?? prev.allowedExtensions,
                }));
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching file storage configuration:', error);
        }
    };

    const fetchPaths = async () => {
        try {
            const response = await apiFetch('/api/Settings/file-storage-paths');
            const data = await response.json();
            if (data.success && data.data) {
                setPaths(prev => ({ ...prev, ...data.data }));
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching path templates:', error);
        }
    };

    const handleConfigChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileSizeChange = (e) => {
        const valueMB = parseInt(e.target.value, 10) || 0;
        setConfig(prev => ({
            ...prev,
            maxFileSizeBytes: valueMB * 1024 * 1024
        }));
    };

    const handlePathChange = (key, value) => {
        setPaths(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            const response = await apiFetch('/api/Settings/file-storage-config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...config,
                    userId: user?.userID || user?.id
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Storage configuration saved');
            } else {
                toast.error(data.message || 'Failed to save configuration');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error saving configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleSavePaths = async () => {
        setSavingPaths(true);
        try {
            const response = await apiFetch('/api/Settings/file-storage-paths', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...paths,
                    userId: user?.userID || user?.id
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Folder structure saved');
            } else {
                toast.error(data.message || 'Failed to save folder structure');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error saving folder structure');
        } finally {
            setSavingPaths(false);
        }
    };

    const handleTestConnection = async () => {
        if (!config.sharePointSiteUrl) {
            toast.warning('Please enter a SharePoint site URL to test');
            return;
        }
        setTesting(true);
        try {
            const response = await apiFetch('/api/Settings/file-storage-config/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sharePointSiteUrl: config.sharePointSiteUrl,
                    sharePointLibraryName: config.sharePointLibraryName
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Connection test successful!');
            } else {
                toast.error(data.message || 'Connection test failed');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    /**
     * Get the root folder name from a path template (first segment before any slash).
     */
    const getRootFolder = (template) => {
        if (!template) return '?';
        const idx = template.indexOf('/');
        return idx > 0 ? template.substring(0, idx) : template;
    };

    const fileSizeMB = Math.round(config.maxFileSizeBytes / (1024 * 1024));

    if (loading) {
        return <div className="fsc-loading">Loading configuration...</div>;
    }

    return (
        <div className="fsc-page">
            <div className="fsc-header">
                <h2>📁 File Storage Configuration</h2>
                <p className="fsc-subtitle">
                    All file attachments are stored in a single SharePoint document library.
                    Each CapTrac module saves into its own subfolder structure automatically.
                </p>
                <div style={{
                    marginTop: '12px',
                    padding: '12px 16px',
                    backgroundColor: '#e7f1ff',
                    border: '1px solid #b6d4fe',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#084298',
                    lineHeight: '1.5'
                }}>
                    <strong>ℹ️ This is the authoritative page for all file storage settings.</strong>{' '}
                    The connection details below are also visible as a read-only reference in <em>App Secrets</em>,
                    but should always be edited here. Changes saved here automatically update the underlying configuration.
                </div>
            </div>

            {/* ── Section 1: Storage Connection ── */}
            <div className="fsc-section">
                <div className="fsc-section-header">
                    <h3><IoCloudDone size={20} /> Storage Connection</h3>
                </div>

                <div className="fsc-form-row">
                    <div className="fsc-field">
                        <label>SharePoint Site URL *</label>
                        <input
                            type="text"
                            name="sharePointSiteUrl"
                            value={config.sharePointSiteUrl}
                            onChange={handleConfigChange}
                            placeholder="https://yourtenant.sharepoint.com/sites/CapTrac"
                        />
                        <small>Full URL to the SharePoint site where files are stored</small>
                    </div>
                    <div className="fsc-field">
                        <label>Document Library Name *</label>
                        <input
                            type="text"
                            name="sharePointLibraryName"
                            value={config.sharePointLibraryName}
                            onChange={handleConfigChange}
                            placeholder="Documents"
                        />
                        <small>The single document library used for all CapTrac files</small>
                    </div>
                </div>

                <div className="fsc-form-row fsc-checkboxes">
                    <label className="fsc-checkbox">
                        <input
                            type="checkbox"
                            name="isEnabled"
                            checked={config.isEnabled}
                            onChange={handleConfigChange}
                        />
                        Enable File Attachments
                    </label>
                </div>

                <div className="fsc-actions-inline">
                    <button
                        className="fsc-btn fsc-btn-secondary"
                        onClick={handleTestConnection}
                        disabled={testing || !config.sharePointSiteUrl}
                    >
                        {testing ? 'Testing...' : '🔗 Test Connection'}
                    </button>
                    {saving && <span style={{ color: '#2563eb', fontSize: '0.85rem' }}>Saving...</span>}
                </div>
            </div>

            {/* ── Section 2: Folder Organization (Visual Tree) ── */}
            <div className="fsc-section">
                <div className="fsc-section-header">
                    <h3><IoFolderOpen size={20} /> Folder Organization</h3>
                    <p className="fsc-section-desc">
                        This is how files are organized inside your SharePoint library.
                        Each module has its own folder tree using immutable IDs (property IDs, asset IDs, etc.)
                        so files are never lost when items are renamed.
                    </p>
                </div>

                {/* Visual folder tree */}
                <div className="fsc-tree">
                    {/* Library root */}
                    <div className="fsc-tree-root">
                        <IoFolderOpen size={20} className="fsc-tree-icon root" />
                        <span className="fsc-tree-label root">
                            {config.sharePointLibraryName || 'Documents'}
                        </span>
                    </div>

                    {/* Base path prefix */}
                    <div className="fsc-tree-level-1">
                        <IoFolder size={18} className="fsc-tree-icon prefix" />
                        <span className="fsc-tree-label prefix">{paths.basePathPrefix || 'CapTrac'}</span>
                        <span className="fsc-tree-hint">Base prefix</span>
                    </div>

                    {/* Module folders */}
                    {MODULE_DEFINITIONS.map(mod => {
                        const template = paths[mod.key] || '';
                        const rootFolder = getRootFolder(template);
                        const isExpanded = expandedModule === mod.key;

                        return (
                            <div key={mod.key} className="fsc-tree-module">
                                <div
                                    className={`fsc-tree-level-2 ${isExpanded ? 'expanded' : ''}`}
                                    onClick={() => setExpandedModule(isExpanded ? null : mod.key)}
                                >
                                    {isExpanded ? <IoChevronDown size={14} /> : <IoChevronForward size={14} />}
                                    <IoFolder size={16} className="fsc-tree-icon module" />
                                    <span className="fsc-tree-module-icon">{mod.icon}</span>
                                    <span className="fsc-tree-label module">{rootFolder}/</span>
                                    <span className="fsc-tree-module-name">{mod.label}</span>
                                </div>

                                {isExpanded && (
                                    <div className="fsc-tree-detail">
                                        <div className="fsc-tree-detail-info">
                                            <p className="fsc-tree-detail-desc">{mod.description}</p>
                                            <div className="fsc-tree-example">
                                                <IoDocument size={14} />
                                                <span>Example path: <code>{paths.basePathPrefix}/{template}</code></span>
                                            </div>
                                        </div>
                                        <div className="fsc-tree-detail-field">
                                            <label>Path Template</label>
                                            <input
                                                type="text"
                                                value={template}
                                                onChange={e => handlePathChange(mod.key, e.target.value)}
                                            />
                                            <small>
                                                Use placeholders: {'{PropertyID}'}, {'{AssetID}'}, {'{Year}'}, {'{Month}'}, {'{ActionItemID}'}, etc.
                                            </small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Base path prefix editor */}
                <div className="fsc-base-prefix">
                    <div className="fsc-field fsc-field-narrow">
                        <label>Base Path Prefix</label>
                        <input
                            type="text"
                            value={paths.basePathPrefix}
                            onChange={e => handlePathChange('basePathPrefix', e.target.value)}
                        />
                        <small>Root folder name within the library — all module folders sit inside this</small>
                    </div>
                </div>

                {savingPaths && <div style={{ color: '#2563eb', fontSize: '0.85rem', padding: '8px 0' }}>Saving folder structure...</div>}
            </div>

            {/* ── Section 3: File Restrictions ── */}
            <div className="fsc-section">
                <div className="fsc-section-header">
                    <h3><IoAlertCircle size={20} /> File Restrictions</h3>
                </div>

                <div className="fsc-form-row">
                    <div className="fsc-field fsc-field-narrow">
                        <label>Maximum File Size (MB)</label>
                        <input
                            type="number"
                            value={fileSizeMB}
                            onChange={handleFileSizeChange}
                            min="1"
                            max="250"
                        />
                        <small>Maximum size for individual file uploads (1–250 MB)</small>
                    </div>
                    <div className="fsc-field">
                        <label>Allowed File Extensions</label>
                        <input
                            type="text"
                            name="allowedExtensions"
                            value={config.allowedExtensions}
                            onChange={handleConfigChange}
                            placeholder=".pdf,.doc,.docx,.jpg,.png"
                        />
                        <small>Comma-separated list of allowed file extensions</small>
                    </div>
                </div>

                {saving && <div style={{ color: '#2563eb', fontSize: '0.85rem', padding: '8px 0' }}>Saving restrictions...</div>}
            </div>

            {/* ── Section 4: Advanced Options (Collapsible) ── */}
            <div className="fsc-section">
                <div
                    className="fsc-section-header fsc-section-collapsible"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                >
                    {showAdvanced ? <IoChevronDown size={18} /> : <IoChevronForward size={18} />}
                    <h3><IoSettings size={20} /> Advanced Options</h3>
                </div>

                {showAdvanced && (
                    <div className="fsc-advanced">
                        <div className="fsc-form-row">
                            <div className="fsc-field fsc-field-narrow">
                                <label>File Name Prefix</label>
                                <select
                                    value={paths.fileNamePrefix}
                                    onChange={e => handlePathChange('fileNamePrefix', e.target.value)}
                                >
                                    <option value="None">None</option>
                                    <option value="Timestamp">Timestamp</option>
                                    <option value="GUID">GUID</option>
                                    <option value="Both">Both</option>
                                </select>
                                <small>Prefix added to file names for uniqueness</small>
                            </div>
                            <div className="fsc-field fsc-field-narrow">
                                <label>Max File Name Length</label>
                                <input
                                    type="number"
                                    value={paths.maxFileNameLength}
                                    onChange={e => handlePathChange('maxFileNameLength', parseInt(e.target.value) || 100)}
                                    min="20"
                                    max="400"
                                />
                                <small>Characters (excluding extension). SharePoint max is 400 for full path.</small>
                            </div>
                            <div className="fsc-field fsc-field-narrow">
                                <label>Temp File Retention (days)</label>
                                <input
                                    type="number"
                                    value={paths.tempFileRetentionDays}
                                    onChange={e => handlePathChange('tempFileRetentionDays', parseInt(e.target.value) || 30)}
                                    min="1"
                                    max="365"
                                />
                                <small>Days before temp files are automatically cleaned up</small>
                            </div>
                        </div>

                        <div className="fsc-form-row fsc-checkboxes">
                            <label className="fsc-checkbox">
                                <input
                                    type="checkbox"
                                    checked={paths.preserveOriginalFileName}
                                    onChange={e => handlePathChange('preserveOriginalFileName', e.target.checked)}
                                />
                                Preserve original file names
                                <small>If unchecked, files are renamed to a generated unique name</small>
                            </label>
                            <label className="fsc-checkbox">
                                <input
                                    type="checkbox"
                                    checked={paths.sanitizeFileNames}
                                    onChange={e => handlePathChange('sanitizeFileNames', e.target.checked)}
                                />
                                Sanitize file names
                                <small>Remove special characters for SharePoint compatibility</small>
                            </label>
                        </div>

                        {savingPaths && <div style={{ color: '#2563eb', fontSize: '0.85rem', padding: '8px 0' }}>Saving advanced options...</div>}
                    </div>
                )}
            </div>

            {/* ── How It Works Info ── */}
            <div className="fsc-info-panel">
                <h4><IoCheckmarkCircle size={18} /> How file storage works</h4>
                <ul>
                    <li>All files go to <strong>one SharePoint library</strong>: &quot;{config.sharePointLibraryName || 'Documents'}&quot;</li>
                    <li>Inside the library, files are organized under <strong>{paths.basePathPrefix}/</strong> using the folder templates above</li>
                    <li>Folder paths use <strong>immutable IDs</strong> (Property ID, Asset ID, etc.) so files are never lost when items are renamed</li>
                    <li>Each module automatically creates its subfolder structure when files are uploaded</li>
                </ul>
            </div>



            <style>{`
                .fsc-page {
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 20px;
                }

                .fsc-loading {
                    text-align: center;
                    padding: 40px;
                    color: #888;
                }

                .fsc-header {
                    margin-bottom: 24px;
                }

                .fsc-header h2 {
                    color: #57565B;
                    margin-bottom: 8px;
                }

                .fsc-subtitle {
                    color: #888;
                    margin: 0;
                    font-size: 0.95rem;
                }

                /* ── Sections ── */
                .fsc-section {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 20px;
                    margin-bottom: 16px;
                }

                .fsc-section-header {
                    margin-bottom: 16px;
                }

                .fsc-section-header h3 {
                    color: #57565B;
                    font-size: 1.1rem;
                    margin: 0 0 4px 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .fsc-section-desc {
                    color: #888;
                    font-size: 0.9rem;
                    margin: 4px 0 0;
                }

                .fsc-section-collapsible {
                    cursor: pointer;
                    user-select: none;
                }

                .fsc-section-collapsible h3 {
                    cursor: pointer;
                }

                /* ── Form Fields ── */
                .fsc-form-row {
                    display: flex;
                    gap: 16px;
                    margin-bottom: 16px;
                    flex-wrap: wrap;
                }

                .fsc-field {
                    flex: 1;
                    min-width: 200px;
                }

                .fsc-field-narrow {
                    max-width: 220px;
                }

                .fsc-field label {
                    display: block;
                    font-weight: 600;
                    color: #57565B;
                    margin-bottom: 6px;
                    font-size: 0.9rem;
                }

                .fsc-field input,
                .fsc-field select {
                    width: 100%;
                    padding: 9px 12px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 0.95rem;
                    transition: border-color 0.2s;
                    box-sizing: border-box;
                }

                .fsc-field input:focus,
                .fsc-field select:focus {
                    outline: none;
                    border-color: #F63040;
                }

                .fsc-field small {
                    display: block;
                    color: #999;
                    font-size: 0.82rem;
                    margin-top: 4px;
                }

                .fsc-checkboxes {
                    flex-direction: column;
                    gap: 8px;
                }

                .fsc-checkbox {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    font-weight: 500;
                    color: #57565B;
                    cursor: pointer;
                    flex-wrap: wrap;
                }

                .fsc-checkbox input[type="checkbox"] {
                    margin-top: 3px;
                    width: auto;
                }

                .fsc-checkbox small {
                    width: 100%;
                    color: #999;
                    font-size: 0.82rem;
                    margin-left: 24px;
                    margin-top: 2px;
                }

                /* ── Actions ── */
                .fsc-actions-inline {
                    display: flex;
                    gap: 12px;
                    margin-top: 8px;
                }

                .fsc-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 9px 18px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    cursor: pointer;
                    border: none;
                    transition: all 0.2s;
                }

                .fsc-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .fsc-btn-primary {
                    background: #F63040;
                    color: #fff;
                }

                .fsc-btn-primary:hover:not(:disabled) {
                    background: #d42a38;
                }

                .fsc-btn-secondary {
                    background: #fff;
                    color: #57565B;
                    border: 1px solid #ddd;
                }

                .fsc-btn-secondary:hover:not(:disabled) {
                    background: #f5f5f5;
                }

                /* ── Visual Folder Tree ── */
                .fsc-tree {
                    background: #fafbfc;
                    border: 1px solid #e8e8e8;
                    border-radius: 8px;
                    padding: 16px 16px 12px 16px;
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    margin-bottom: 16px;
                }

                .fsc-tree-root {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 0;
                    font-weight: 700;
                    font-size: 1rem;
                    color: #0066cc;
                }

                .fsc-tree-icon.root { color: #0066cc; }
                .fsc-tree-icon.prefix { color: #e6a817; }
                .fsc-tree-icon.module { color: #57565B; }

                .fsc-tree-label.root { color: #0066cc; }
                .fsc-tree-label.prefix { color: #e6a817; font-weight: 600; }
                .fsc-tree-label.module { color: #57565B; font-family: 'Consolas', 'Courier New', monospace; font-size: 0.9rem; }

                .fsc-tree-hint {
                    color: #bbb;
                    font-size: 0.8rem;
                    font-style: italic;
                    margin-left: 8px;
                }

                .fsc-tree-level-1 {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 0 4px 24px;
                    border-left: 2px solid #e0e0e0;
                    margin-left: 9px;
                }

                .fsc-tree-module {
                    border-left: 2px solid #e0e0e0;
                    margin-left: 33px;
                }

                .fsc-tree-level-2 {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 5px 8px;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: background 0.15s;
                }

                .fsc-tree-level-2:hover {
                    background: #f0f0f0;
                }

                .fsc-tree-level-2.expanded {
                    background: #eef5ff;
                }

                .fsc-tree-module-icon {
                    font-size: 0.85rem;
                }

                .fsc-tree-module-name {
                    color: #888;
                    font-size: 0.85rem;
                    margin-left: 4px;
                }

                .fsc-tree-detail {
                    margin-left: 28px;
                    padding: 8px 12px 12px;
                    background: #fff;
                    border: 1px solid #e8e8e8;
                    border-radius: 6px;
                    margin-bottom: 4px;
                    margin-right: 4px;
                }

                .fsc-tree-detail-info {
                    margin-bottom: 10px;
                }

                .fsc-tree-detail-desc {
                    color: #666;
                    font-size: 0.88rem;
                    margin: 0 0 6px;
                }

                .fsc-tree-example {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #888;
                    font-size: 0.85rem;
                }

                .fsc-tree-example code {
                    background: #f5f5f5;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-family: 'Consolas', 'Courier New', monospace;
                    font-size: 0.85rem;
                    color: #0066cc;
                }

                .fsc-tree-detail-field {
                    margin-top: 8px;
                }

                .fsc-tree-detail-field label {
                    display: block;
                    font-weight: 600;
                    font-size: 0.82rem;
                    color: #57565B;
                    margin-bottom: 4px;
                }

                .fsc-tree-detail-field input {
                    width: 100%;
                    padding: 7px 10px;
                    border: 1px solid #ddd;
                    border-radius: 5px;
                    font-family: 'Consolas', 'Courier New', monospace;
                    font-size: 0.88rem;
                    box-sizing: border-box;
                }

                .fsc-tree-detail-field input:focus {
                    outline: none;
                    border-color: #F63040;
                }

                .fsc-tree-detail-field small {
                    display: block;
                    color: #bbb;
                    font-size: 0.78rem;
                    margin-top: 3px;
                }

                /* ── Base Prefix ── */
                .fsc-base-prefix {
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 1px solid #eee;
                }

                /* ── Info Panel ── */
                .fsc-info-panel {
                    background: #f0f8ff;
                    border: 1px solid #c8e1ff;
                    border-radius: 8px;
                    padding: 16px 20px;
                    margin-bottom: 16px;
                }

                .fsc-info-panel h4 {
                    color: #0066cc;
                    margin: 0 0 10px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 1rem;
                }

                .fsc-info-panel ul {
                    margin: 0;
                    padding-left: 20px;
                    color: #555;
                    font-size: 0.9rem;
                    line-height: 1.6;
                }

                .fsc-info-panel li {
                    margin-bottom: 2px;
                }

                /* ── Advanced ── */
                .fsc-advanced {
                    margin-top: 12px;
                }
            `}</style>
        </div>
    );
};

export default FileStorageConfigPage;
